import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './Tabs';
import { SessionSecretsPanel } from './SessionSecretsPanel';
import { DatabasePanel } from './DatabasePanel';
import { GitHubSettingsPanel } from './GitHubSettingsPanel';
import {
  Key,
  Settings as SettingsIcon,
  Sliders,
  Database,
  Github,
  Cloud,
  Loader2,
  CheckCircle,
  XCircle,
  Lightbulb,
} from 'lucide-react';
import { toast } from 'sonner';
import { AiAutonomySettings } from './AiAutonomySettings';
import styles from './SessionSettingsPanel.module.css';

function GeneralSettingsContent() {
  const [deploying, setDeploying] = useState(false);
  const [deployResults, setDeployResults] = useState<
    { worker: string; status: string; output?: string; error?: string }[] | null
  >(null);

  const handleDeploy = async () => {
    setDeploying(true);
    setDeployResults(null);
    try {
      const res = await fetch('/_api/admin/deploy-workers', { method: 'POST' });
      const data = await res.json();
      setDeployResults(data.results || []);
      if (data.success) {
        toast.success('Workers deployed successfully');
      } else {
        toast.error('Some workers failed to deploy');
      }
    } catch (err: any) {
      toast.error('Deploy failed: ' + (err.message || 'Network error'));
      setDeployResults([{ worker: 'all', status: 'error', error: err.message || 'Network error' }]);
    } finally {
      setDeploying(false);
    }
  };

  return (
    <div className={styles.generalSettings}>
      <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}
        >
          <Cloud size={18} style={{ color: 'var(--accent)' }} />
          <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Deploy Workers</span>
        </div>
        <p
          style={{
            fontSize: '0.82rem',
            color: 'var(--text-secondary)',
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          Build frontend, sync to KV, and deploy Cloudflare Workers. This ensures the latest code is
          live on tasking.tech.
        </p>
        <button
          onClick={handleDeploy}
          disabled={deploying}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.6rem 1.2rem',
            background: deploying ? 'var(--bg-tertiary)' : 'var(--accent)',
            color: deploying ? 'var(--text-secondary)' : '#fff',
            border: 'none',
            borderRadius: 'var(--radius)',
            cursor: deploying ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '0.85rem',
            transition: 'all 0.2s ease',
          }}
        >
          {deploying ? <Loader2 size={16} className="animate-spin" /> : <Cloud size={16} />}
          {deploying ? 'Deploying...' : 'Deploy All Workers'}
        </button>

        {deployResults && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              marginTop: '0.25rem',
            }}
          >
            {deployResults.map((r, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  background:
                    r.status === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.8rem',
                }}
              >
                {r.status === 'success' ? (
                  <CheckCircle
                    size={14}
                    style={{ color: '#22c55e', marginTop: 2, flexShrink: 0 }}
                  />
                ) : (
                  <XCircle size={14} style={{ color: '#ef4444', marginTop: 2, flexShrink: 0 }} />
                )}
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', minWidth: 0 }}
                >
                  <span style={{ fontWeight: 600 }}>{r.worker}</span>
                  {r.error && (
                    <span style={{ color: '#ef4444', wordBreak: 'break-all' }}>
                      {r.error.slice(0, 300)}
                    </span>
                  )}
                  {r.output && r.status === 'success' && (
                    <span style={{ color: 'var(--text-secondary)', wordBreak: 'break-all' }}>
                      {r.output.slice(-200)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface SessionSettingsPanelProps {
  sessionId: string;
  isOwner?: boolean;
}

export const SessionSettingsPanel: React.FC<SessionSettingsPanelProps> = ({
  sessionId,
  isOwner = false,
}) => {
  return (
    <div className={styles.container}>
      <Tabs defaultValue="github" className={styles.tabs}>
        <TabsList className={styles.tabsList}>
          <TabsTrigger value="github" className={styles.tabTrigger}>
            <Github size={14} />
            <span>GitHub</span>
          </TabsTrigger>
          <TabsTrigger value="secrets" className={styles.tabTrigger}>
            <Key size={14} />
            <span>Secrets</span>
          </TabsTrigger>
          <TabsTrigger value="database" className={styles.tabTrigger}>
            <Database size={14} />
            <span>Database</span>
          </TabsTrigger>
          <TabsTrigger value="ai" className={styles.tabTrigger}>
            <Lightbulb size={14} />
            <span>AI</span>
          </TabsTrigger>
          <TabsTrigger value="general" className={styles.tabTrigger}>
            <Sliders size={14} />
            <span>General</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="github" className={styles.tabContent}>
          <GitHubSettingsPanel sessionId={sessionId} />
        </TabsContent>

        <TabsContent value="secrets" className={styles.tabContent}>
          <SessionSecretsPanel sessionId={sessionId} isOwner={isOwner} />
        </TabsContent>

        <TabsContent value="database" className={styles.tabContent}>
          <DatabasePanel sessionId={sessionId} isOwner={isOwner} />
        </TabsContent>

        <TabsContent value="ai" className={styles.tabContent}>
          <AiAutonomySettings sessionId={sessionId} />
        </TabsContent>

        <TabsContent value="general" className={styles.tabContent}>
          <GeneralSettingsContent />
        </TabsContent>
      </Tabs>
    </div>
  );
};
